import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export default function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlog = async () => {
      setLoading(true);
      try {
        const data = await api.getBlog(id);
        setBlog(data);
      } catch (err) {
        toast.error('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) {
      return 'N/A';
    }
  };

  return (
    <div className="min-h-screen">
      <Header />

      <section className="relative pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center text-sm text-blue-100 hover:text-white mb-6"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to articles
          </button>

          {loading && (
            <p className="text-gray-200">Loading article...</p>
          )}

          {!loading && !blog && (
            <p className="text-gray-200">Blog post not found.</p>
          )}

          {blog && (
            <>
              <div className="mb-4 text-sm font-semibold text-blue-300 uppercase tracking-wide">
                {blog.category || 'Insights'}
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="blog-post-title">
                {blog.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-blue-100 mb-8">
                {blog.author && (
                  <div className="flex items-center gap-1">
                    <User size={16} />
                    <span>{blog.author}</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar size={16} />
                  <span>{formatDate(blog.created_at)}</span>
                </div>
                {blog.readTime && <span>{blog.readTime}</span>}
              </div>
            </>
          )}
        </div>
      </section>

      {blog && (
        <section className="py-10 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            {blog.image && (
              <Card className="border-none shadow-md mb-8">
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="w-full h-72 object-cover"
                  loading="lazy"
                />
              </Card>
            )}

            <Card className="border-none shadow-md">
              <CardContent className="p-8">
                {blog.excerpt && (
                  <p className="text-lg text-gray-700 mb-6 font-medium">{blog.excerpt}</p>
                )}
                <div
                  className="prose max-w-none text-gray-800"
                  dangerouslySetInnerHTML={{ __html: blog.content || '' }}
                  data-testid="blog-post-content"
                />
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      <section className="py-10 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <Button
            variant="outline"
            onClick={() => navigate('/blog')}
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to all articles
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
